import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { createFileRoute } from "@tanstack/react-router";
import { Camera, CloudOff, Crop, Download, History, Send } from "lucide-react";
import QRCode from "qrcode";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** `/download/scanner`: Netlion Scanner, the Android app for photographing documents. */

export const Route = createFileRoute("/_app/download_/scanner")({
  component: ScannerDownloadPage,
});

const FEATURES: Array<{ icon: ReactNode; key: string }> = [
  { icon: <Camera className="h-5 w-5" />, key: "photograph" },
  { icon: <Crop className="h-5 w-5" />, key: "cleanup" },
  { icon: <Send className="h-5 w-5" />, key: "send" },
  { icon: <CloudOff className="h-5 w-5" />, key: "offline" },
  { icon: <History className="h-5 w-5" />, key: "recent" },
];

const APK_PATH = "/app/netlion-scanner.apk";

function ScannerDownloadPage() {
  const { t } = useTranslation();
  const apkUrl = `${window.location.origin}${APK_PATH}`;
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    void QRCode.toDataURL(apkUrl, { margin: 1, width: 360 })
      .then(setQr)
      .catch(() => setQr(null));
  }, [apkUrl]);

  return (
    <div className="mx-auto max-w-4xl space-y-10 py-6" data-testid="scanner-download-page">
      <section className="flex flex-col gap-6 md:flex-row md:items-start">
        <div className="flex-1 space-y-4">
          <div className="flex items-center gap-3">
            <img src="/apps/scanner.svg" alt="" className="h-14 w-14" />
            <div>
              <h1 className="text-2xl font-semibold">{t("miscScanner.title")}</h1>
              <p className="text-muted-foreground">{t("miscScanner.intro")}</p>
            </div>
          </div>
          <Button asChild>
            <a href={APK_PATH}>
              <Download className="mr-2 h-4 w-4" />
              {t("miscScanner.download")}
            </a>
          </Button>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            <li>{t("miscScanner.step1")}</li>
            <li>{t("miscScanner.step2")}</li>
            <li>{t("miscScanner.step3")}</li>
          </ol>
        </div>
        {qr && (
          <img
            src={qr}
            alt={t("miscScanner.qrAlt")}
            className="h-44 w-44 self-center rounded-lg border bg-white p-2 md:self-start"
          />
        )}
      </section>

      <section className="grid gap-3 sm:grid-cols-2">
        {FEATURES.map((f) => (
          <Card key={f.key}>
            <CardContent className="flex gap-3 p-4">
              <div className="mt-0.5 shrink-0 text-primary">{f.icon}</div>
              <div className="space-y-1">
                <p className="font-medium">{t(`miscScanner.features.${f.key}.title`)}</p>
                <p className="text-sm text-muted-foreground">
                  {t(`miscScanner.features.${f.key}.body`)}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </section>
    </div>
  );
}
